import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../../hooks/useCart';
import CartButton from '../../../components/feature/CartButton';
import AddToCartToast from '../../../components/feature/AddToCartToast';

export default function YakgwaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedOption, setSelectedOption] = useState('small');
  const [showToast, setShowToast] = useState(false);
  const { addToCart } = useCart();

  // 페이지 진입 시 상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* -------------------------------------------------
   * Scroll listener – useEffect with cleanup
   * ------------------------------------------------- */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const options = [
    { id: 'small', name: '소형 (10개)', price: 15000 },
    { id: 'medium', name: '중형 (20개)', price: 28000 },
    { id: 'large', name: '대형 (30개)', price: 40000 },
  ];

  const selectedPrice = options.find((opt) => opt.id === selectedOption)?.price ?? 15000;
  const totalPrice = selectedPrice * quantity;

  const handleQuantityChange = (type: 'increase' | 'decrease') => {
    if (type === 'increase') {
      setQuantity((prev) => prev + 1);
    } else if (type === 'decrease' && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const handleAddToCart = () => {
    const selectedOpt = options.find((opt) => opt.id === selectedOption);
    addToCart({
      id: 'yakgwa',
      name: '약과',
      option: selectedOpt?.name || '',
      price: selectedOpt?.price || 15000,
      quantity,
      image: 'https://public.readdy.ai/ai/img_res/2eca62b541949ebdcf7d76fa7cf0906c.jpg',
    });
    setShowToast(true);
    setQuantity(1);
  };

  const relatedProducts = [
    {
      id: 'yugwa',
      name: '유과',
      description: '찹쌀을 정성껏 튀겨 만든 바삭하고 고소한 전통 유과',
      price: 18000,
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Korean%20yugwa%20rice%20puff%20snacks%20colorful%20assortment%20beautifully%20arranged%20on%20elegant%20white%20ceramic%20plate%2C%20light%20airy%20texture%2C%20pastel%20colors%2C%20detailed%20close-up%20shot%2C%20simple%20cream%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20artisanal%20handmade%20quality&width=400&height=400&seq=yugwa-related-002&orientation=squarish',
      link: '/product/yugwa',
      tag: '프리미엄',
    },
    {
      id: 'giftset',
      name: '한과 선물 세트',
      description: '약과와 유과를 고급스럽게 구성한 선물 세트',
      price: 35000,
      image: 'https://readdy.ai/api/search-image?query=Premium%20Korean%20traditional%20confectionery%20gift%20set%20in%20elegant%20wooden%20box%2C%20assorted%20hangwa%20varieties%20including%20yakgwa%20and%20yugwa%20beautifully%20arranged%2C%20luxurious%20presentation%20with%20traditional%20bojagi%20fabric%2C%20simple%20cream%20background%2C%20professional%20product%20photography%2C%20high-end%20gift%20display&width=400&height=400&seq=giftset-related-002&orientation=squarish',
      link: '/product/gift-set',
      tag: '명절 선물 추천',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* 네비게이션 */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/f77122aa20e12351d50b895437d7646f/26f7e4287336b6a14c4caa48b3ce1ec3.png"
                alt="초이담 참새한과"
                className="h-12 w-auto cursor-pointer"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                홈
              </Link>
              <a
                href="/#about"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                브랜드 소개
              </a>
              <a
                href="/#menu"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                메뉴
              </a>
              <a
                href="/#contact"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                문의
              </a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CartButton isScrolled={isScrolled} />
            <button className="px-6 py-2.5 bg-gray-900 text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">
              주문하기
            </button>
          </div>
        </div>
      </nav>

      {/* 상품 상세 섹션 */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* 이미지 영역 */}
            <div className="sticky top-32">
              <div className="bg-amber-50 rounded-3xl overflow-hidden aspect-square">
                <img
                  src="https://readdy.ai/api/search-image?query=Traditional%20Korean%20yakgwa%20honey%20cookies%20beautifully%20arranged%20on%20elegant%20white%20ceramic%20plate%2C%20golden%20brown%20glossy%20surface%2C%20detailed%20close-up%20shot%2C%20simple%20cream%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20artisanal%20handmade%20quality&width=800&height=800&seq=yakgwa-detail-001&orientation=squarish"
                  alt="약과"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* 상품 정보 영역 */}
            <div>
              <div className="mb-8">
                <span className="inline-block bg-amber-100 px-4 py-2 rounded-full text-sm text-amber-900 font-medium mb-4">
                  인기 메뉴
                </span>
                <h1
                  className="text-5xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: "'Noto Serif KR', serif" }}
                >
                  약과
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  꿀과 참기름의 고소한 향이 어우러진 전통 약과입니다.
                  주문 제작 방식으로 신선하게 만들어 바삭하면서도 촉촉한 식감을 자랑합니다.
                </p>
              </div>

              {/* 가격 */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <p className="text-4xl font-bold text-gray-900">
                  {selectedPrice.toLocaleString()}원
                </p>
              </div>

              {/* 옵션 선택 */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">구성 선택</h3>
                <div className="grid grid-cols-3 gap-3">
                  {options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSelectedOption(option.id)}
                      className={`px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        selectedOption === option.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* 수량 선택 */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">수량</h3>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleQuantityChange('decrease')}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-gray-900 text-xl" />
                  </button>
                  <span className="text-2xl font-bold text-gray-900 w-16 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange('increase')}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-gray-900 text-xl" />
                  </button>
                </div>
              </div>

              {/* 총 가격 */}
              <div className="mb-8 p-6 bg-amber-50 rounded-2xl">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-gray-900">총 금액</span>
                  <span className="text-3xl font-bold text-gray-900">
                    {totalPrice.toLocaleString()}원
                  </span>
                </div>
              </div>

              {/* 주문 / 문의 버튼 */}
              <button 
                onClick={handleAddToCart}
                className="w-full px-8 py-5 bg-gray-900 text-white rounded-full text-lg font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer mb-4"
              >
                장바구니 담기
              </button>
              <button className="w-full px-8 py-5 bg-white text-gray-900 border-2 border-gray-900 rounded-full text-lg font-medium hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer">
                문의하기
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 상세 정보 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* 원재료 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-plant-line text-amber-900 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">원재료</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 밀가루 (국내산)</li>
                <li>• 꿀 (국내산)</li>
                <li>• 참기름 (국내산)</li>
                <li>• 생강즙</li>
                <li>• 계피가루</li>
                <li>• 소금</li>
              </ul>
            </div>

            {/* 보관방법 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-fridge-line text-amber-900 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">보관방법</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                직사광선을 피해 서늘한 곳에 보관해 주세요.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                개봉 후에는 밀폐 용기에 담아 냉장 보관하시면 더 오래 신선하게 드실 수 있습니다.
              </p>
              <p className="text-sm text-amber-900 font-medium">유통기한: 제조일로부터 30일</p>
            </div>

            {/* 특징 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-star-line text-amber-900 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">특징</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1" />
                  <span>주문 제작 방식으로 신선함 보장</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1" />
                  <span>100% 국내산 재료 사용</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1" />
                  <span>인공 첨가물 무첨가</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1" />
                  <span>정성스런 수제 포장</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 관련 상품 추천 섹션 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2
              className="text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Noto Serif KR', serif" }}
            >
              함께 즐기면 좋은 한과
            </h2>
            <p className="text-gray-600">약과와 함께 더욱 풍성한 한과의 맛을 경험해 보세요</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {relatedProducts.map((product) => (
              <Link
                key={product.id}
                to={product.link}
                className="group bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-amber-100 px-3 py-1 rounded-full text-xs text-amber-900 font-medium mb-3">
                    {product.tag}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {product.price.toLocaleString()}원~
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="bg-gradient-to-b from-amber-900 to-amber-950 text-white mx-5 mb-5 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-12 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <h3
                className="text-3xl font-bold leading-tight mb-6"
                style={{ fontFamily: "'Noto Serif KR', serif" }}
              >
                초이담과 함께
                <br />
                특별한 선물을
              </h3>
            </div>

            <div>
              <h4 className="font-medium text-white mb-6">메뉴</h4>
              <ul className="space-y-4 text-amber-100">
                <li>
                  <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                    홈
                  </Link>
                </li>
                <li>
                  <a href="/#about" className="hover:text-white transition-colors cursor-pointer">
                    브랜드 소개
                  </a>
                </li>
                <li>
                  <a href="/#menu" className="hover:text-white transition-colors cursor-pointer">
                    메뉴
                  </a>
                </li>
                <li>
                  <a href="/#contact" className="hover:text-white transition-colors cursor-pointer">
                    문의
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-white mb-6">소셜 미디어</h4>
              <ul className="space-y-4 text-amber-100">
                <li className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg" />
                  <span>Instagram</span>
                </li>
                <li className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-article-line text-lg" />
                  <span>Blog</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-white mb-3">문의</h4>
              <p className="text-sm text-amber-100 mb-2">전화: 02-1234-5678</p>
              <p className="text-sm text-amber-100">이메일: info@choidam.com</p>
            </div>
          </div>

          <div className="relative pt-8 border-t border-amber-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-amber-200">
                © 2024 초이담 참새한과. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-amber-200 hover:text-white transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      <AddToCartToast 
        isVisible={showToast} 
        onClose={() => setShowToast(false)} 
        productName="약과" 
      />
    </div>
  );
}
